import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Stack,
  Divider,
  CircularProgress,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined'
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import BreadcrumbsComponent from '../../components/common/GlobalBreadcrumb'
import { RootState } from '../../redux/store'
import { useAppDispatch } from '../../redux/store'
import { formatTitle } from '../../utils/formatTitle'
import { canAccessLicenseModule } from '../../utils/licenseAccess'
import {
  COMMERCIAL_PACKS,
  PACK_FEATURES,
  CommercialPackKey,
  featureIncludedInPack,
  featureIsSoon,
  toCommercialPack,
} from '../../utils/licensePacks'
import {
  fetchLicense,
  fetchLicensePlans,
} from '../../redux/license/licenseActions'
import {
  selectLicense,
  selectLicenseLoading,
  selectLicensePlansLoading,
} from '../../redux/license/licenseSelectors'

const PACK_ICONS: Record<CommercialPackKey, React.ReactNode> = {
  standard: <ShieldOutlinedIcon />,
  business: <BusinessCenterOutlinedIcon />,
  premium: <WorkspacePremiumOutlinedIcon />,
}

const LicensePage = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const license = useSelector(selectLicense)
  const licenseLoading = useSelector(selectLicenseLoading)
  const plansLoading = useSelector(selectLicensePlansLoading)
  const roles = useSelector((state: RootState) => state.auth.roles)
  const canManage = canAccessLicenseModule(roles, license?.isOwner)

  useEffect(() => {
    dispatch(fetchLicense())
    dispatch(fetchLicensePlans())
  }, [dispatch])

  useEffect(() => {
    // Wait until license is loaded so isOwner is known before redirecting.
    if (licenseLoading) return
    if (license && !canManage) {
      navigate('/', { replace: true })
    }
  }, [canManage, license, licenseLoading, navigate])

  const handleManagePlan = () => {
    if (license?.organizationId) {
      navigate(`/account/organizations/${license.organizationId}/billing`)
    } else {
      navigate('/account/login')
    }
  }

  const breadcrumbs = [
    { label: t('analysis.riskAnalysis.breadcrumbs.home'), path: '/' },
    { label: t('licensePage.title'), path: '/license' },
  ]

  const formatDate = (value: string | null | undefined) =>
    value
      ? new Date(value).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : '—'

  const isExpired =
    license && license.validTo && new Date(license.validTo) < new Date()
  const statusLabel = !license
    ? null
    : !license.active
      ? t('licensePage.status.suspended')
      : isExpired
        ? t('licensePage.status.expired')
        : t('licensePage.status.active')
  const statusColor = !license
    ? 'default'
    : !license.active || isExpired
      ? 'error'
      : 'success'

  const currentPack = toCommercialPack(license?.planKey)
  const currentAccent =
    COMMERCIAL_PACKS.find((p) => p.key === currentPack)?.accent ||
    COMMERCIAL_PACKS[0].accent

  if (!canManage) return null

  return (
    <div className="page-content">
      <div className="breadcrumbs flex-between-center">
        <BreadcrumbsComponent breadcrumbs={breadcrumbs} />
      </div>
      <h3 className="!m-0">{formatTitle(t('licensePage.title'))}</h3>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
        {t('licensePage.subtitle')}
      </Typography>

      <Paper
        sx={{
          p: 3,
          borderRadius: '12px',
          border: '1px solid #e7ecf3',
          boxShadow: 'none',
          mb: 3,
        }}
      >
        {licenseLoading && !license ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : license ? (
          <Stack spacing={2.5}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1.5}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '10px',
                    bgcolor: currentAccent.bg,
                    color: currentAccent.text,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {PACK_ICONS[currentPack]}
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    {t(`license.packs.${currentPack}`, currentPack)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('licensePage.currentPlanLabel')}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                {statusLabel && (
                  <Chip
                    label={statusLabel}
                    color={statusColor as 'default' | 'error' | 'success'}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                )}
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<OpenInNewIcon fontSize="small" />}
                  onClick={handleManagePlan}
                  sx={{
                    borderRadius: '8px',
                    borderColor: '#00146d',
                    color: '#00146d',
                  }}
                >
                  {t('licensePage.managePlan', 'Manage your plan')}
                </Button>
              </Stack>
            </Stack>

            <Divider />

            <Stack direction="row" spacing={1} alignItems="center">
              <EventOutlinedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {t('licensePage.validity')}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatDate(license.validFrom)} — {formatDate(license.validTo)}
                </Typography>
              </Box>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              {t('licensePage.pricingNote', {
                defaultValue:
                  'Pricing on request — an offer built for your context and cyber maturity level.',
              })}
            </Typography>
          </Stack>
        ) : (
          <Typography color="text.secondary">
            {t('licensePage.noLicense')}
          </Typography>
        )}
      </Paper>

      <Typography variant="h6" fontWeight={700} mb={0.5}>
        {t('licensePage.availablePlans')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t('licensePage.packsIntro', {
          defaultValue:
            'Standard or Premium — same GRC core, Premium adds RGPD, onboarding and phone support.',
        })}
      </Typography>

      {plansLoading && !license ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2.5,
          }}
        >
          {COMMERCIAL_PACKS.map((pack) => {
            const isCurrent = currentPack === pack.key
            const features = PACK_FEATURES.filter((f) =>
              featureIncludedInPack(f, pack.key)
            )
            return (
              <Paper
                key={pack.key}
                sx={{
                  p: 3,
                  borderRadius: '14px',
                  border: `2px solid ${isCurrent ? pack.accent.border : '#e7ecf3'}`,
                  boxShadow: isCurrent ? `0 0 0 3px ${pack.accent.bg}` : 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: pack.accent.bg,
                      color: pack.accent.text,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {PACK_ICONS[pack.key]}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t(`license.packs.${pack.key}`)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t(`license.packTaglines.${pack.key}`)}
                    </Typography>
                  </Box>
                </Stack>

                <Divider />

                <Stack spacing={0.75} flexGrow={1}>
                  {features.map((feature) => {
                    const soon = featureIsSoon(feature)
                    return (
                      <Stack
                        key={feature.id}
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                      >
                        {soon ? (
                          <ScheduleOutlinedIcon
                            sx={{ fontSize: 18, color: '#c98a16', mt: '1px' }}
                          />
                        ) : (
                          <CheckCircleOutlineIcon
                            sx={{ fontSize: 18, color: '#0d9b87', mt: '1px' }}
                          />
                        )}
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {t(`license.packFeatures.${feature.labelKey}`)}
                          </Typography>
                          {soon ? (
                            <Typography
                              variant="caption"
                              sx={{ color: '#c98a16', fontWeight: 600 }}
                            >
                              {t('licensePage.comingSoon', 'Coming soon')}
                            </Typography>
                          ) : null}
                        </Box>
                      </Stack>
                    )
                  })}
                  {pack.key !== 'premium' ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: 0.55 }}>
                      <CancelOutlinedIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                      <Typography variant="body2">
                        {pack.key === 'standard'
                          ? t('license.packFeatures.businessAndPremiumExtras', {
                              defaultValue:
                                'AI evidence, integrations, RGPD, onboarding, phone support',
                            })
                          : t('license.packFeatures.premiumExtras', {
                              defaultValue:
                                'RGPD module, guided onboarding, phone support',
                            })}
                      </Typography>
                    </Stack>
                  ) : null}
                </Stack>

                {isCurrent ? (
                  <Chip
                    label={t('licensePage.currentPlanChip')}
                    sx={{
                      alignSelf: 'flex-start',
                      fontWeight: 600,
                      bgcolor: pack.accent.bg,
                      color: pack.accent.text,
                    }}
                  />
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleManagePlan}
                    sx={{
                      bgcolor: '#00146d',
                      '&:hover': { bgcolor: '#011848' },
                      borderRadius: '8px',
                    }}
                  >
                    {t('licensePage.managePlan', 'Manage your plan')}
                  </Button>
                )}
              </Paper>
            )
          })}
        </Box>
      )}
    </div>
  )
}

export default LicensePage
